<template>
  <!-- 带左侧标题（leftTitle）的样式 -->
  <div class="scrm-virtual-select row flex-center" v-if="!hideleftTitle">
    <div class="font-666 bg-white left-label">{{ leftTitle }}</div>
    <el-select-v2
      ref="currentSelect"
      class="fw100"
      :clearable="clearable"
      :filterable="filterable"
      :placeholder="placeholder"
      :options="selfoption"
      :props="selectFieldProps"
      :loading="loading"
      :filter-method="handlePinyinFilter"
      v-model="self[modelval]"
      v-bind="$attrs"
      @clear="resetOption"
      @visible-change="handleVisibleChange"
      @focus="handleSelectFocus"
      @blur="handleSelectBlur"
    />
  </div>
  <!-- 仅下拉框，不展示左侧标题 -->
  <div v-else>
    <el-select-v2
      ref="currentSelect"
      class="fw100"
      :clearable="clearable"
      :filterable="filterable"
      :placeholder="placeholder"
      :options="selfoption"
      :props="selectFieldProps"
      :loading="loading"
      :filter-method="handlePinyinFilter"
      v-model="self[modelval]"
      v-bind="$attrs"
      @clear="resetOption"
      @visible-change="handleVisibleChange"
      @focus="handleSelectFocus"
      @blur="handleSelectBlur"
    />
  </div>
</template>

<script>
import func from "@/util/func.js";
import PinyinMatch from "pinyin-match";

export default {
  name: "virtualPcSelect",
  inheritAttrs: false,
  props: {
    self: {
      type: Object,
      required: true,
    },
    modelval: {
      // 父级 self 对象上对应的字段名
      type: String,
      required: false,
    },
    options: {
      type: Array,
      required: false,
      default: () => [],
    },
    label: {
      // 选项中作为 label 显示的字段名
      type: String,
      default: "label",
    },
    value: {
      // 选项中作为 value 取值的字段名
      type: String,
      default: "value",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: "请选择",
    },
    leftTitle: {
      type: String,
      default: "",
    },
    hideleftTitle: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["getOption", "valblur"],
  data() {
    return {
      // 当前下拉展示的选项列表（包含筛选后的结果）
      selfoption: [],
      // 选项原始备份，过滤完成后用于还原
      selfoptionback: [],
      // 是否首次加载，用于配合 getOption 事件触发懒加载
      isFirstLoad: true,
      loading: true,
    };
  },
  computed: {
    // 通过 props 字段映射，告知 el-select-v2 哪个字段是 value / label / disabled
    selectFieldProps() {
      return {
        value: this.value,
        label: this.label,
        disabled: "disabled",
      };
    },
  },
  watch: {
    // 父级传入的 options 变化时，重置内部备份与渲染列表
    options(newOptionList) {
      this.selfoption = func.copyArr(newOptionList);
      this.selfoptionback = func.copyArr(newOptionList);
      this.loading = false;
      this.isFirstLoad = false;
    },
  },
  mounted() {
    this.$nextTick(() => {
      // 已有默认值但 options 还未加载，主动触发 getOption 让父级补数据
      if (func.notEmpty(this.self[this.modelval])) {
        this.emitGetOption();
      }
      if (this.options.length > 0) {
        this.selfoption = func.copyArr(this.options);
        this.selfoptionback = func.copyArr(this.options);
        this.loading = false;
        this.isFirstLoad = false;
      }
    });
  },
  methods: {
    /**
     * el-select-v2 的筛选回调：使用拼音匹配过滤备份选项
     * 注意：与 el-select 不同，select-v2 的 filter-method 不需要返回值，
     * 通过修改绑定的 options 数据源即可触发列表刷新。
     */
    handlePinyinFilter(keyword) {
      if (keyword) {
        const filteredList = [];
        this.selfoptionback.forEach((optionItem) => {
          const matched = PinyinMatch.match(optionItem[this.label], keyword);
          if (matched) {
            filteredList.push(optionItem);
          }
        });
        this.selfoption = func.copyArr(filteredList);
      } else {
        // 关键字为空时还原原始列表
        this.selfoption = func.copyArr(this.selfoptionback);
      }
    },

    /** 重置 option（清空/可见状态变化时调用） */
    resetOption() {
      this.selfoption = func.copyArr(this.selfoptionback);
    },

    /**
     * 下拉显隐回调：关闭/重新打开时把搜索过滤过的列表还原
     * 与原 pcSelect.vue 的 @visible-change="resizeOptions" 保持一致
     */
    handleVisibleChange() {
      this.resetOption();
    },

    /** 第一次聚焦时通知父级加载数据 */
    handleSelectFocus() {
      if (this.isFirstLoad && this.options.length === 0) {
        this.emitGetOption();
      }
    },

    /** 失焦事件，沿用原组件的对外事件名 valblur */
    handleSelectBlur() {
      this.$emit("valblur");
    },

    /** 主动让选择器失焦（供父组件 ref 调用） */
    selectblur() {
      const selectRef = this.$refs.currentSelect;
      if (selectRef && typeof selectRef.blur === "function") {
        selectRef.blur();
      }
    },

    /** 触发父级懒加载 options 数据 */
    emitGetOption() {
      this.$emit("getOption");
    },

    /** 兼容 pcSelect.vue 的 clear 方法：重置内部状态 */
    clear() {
      this.selfoption = [];
      this.selfoptionback = [];
      this.isFirstLoad = true;
      this.loading = true;
    },
  },
};
</script>

<style scoped lang="scss">
.scrm-virtual-select {
  // 关键样式与 pcSelect.vue 完全对齐，保证两者在搜索区视觉一致
  :deep(.el-select__wrapper) {
    box-shadow: none !important;
    border: 1px solid #e6e6e6;
    border-left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    box-sizing: border-box;
    height: 34px;

    &.is-focused {
      box-shadow: none !important;

      .el-select__input-wrapper {
        box-shadow: none !important;
      }
    }
  }

  :deep(.el-select__tags) {
    flex-wrap: nowrap !important;
  }

  :deep(.el-select__tags-text) {
    max-width: 100px !important;
  }

  .left-label {
    font-size: 14px;
    height: 34px;
    line-height: 32px;
    border: 1px solid #e6e6e6;
    border-right: 0;
    box-sizing: border-box;
    padding: 0 10px;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    white-space: nowrap;
    color: #666;
  }
}
</style>
